import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography
} from '@mui/material';

import TablePaginationActions from '../../../../../components/utils/TablePaginationActions';
import useTablePaginationInit from '../../../../../components/hooks/useTablePaginationInit';
import TableTimeStamps from '../../../../../components/utils/TableTimeStamps';
import Copyable from '../../../../../components/utils/Copyable';
import TopBar from '../../../../../components/utils/TopBar';
import NA from '../../../../../components/utils/NA';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';

import {
  GetAssemblySessionsBIZcoreTD
} from '../../../../../redux/actions/business/core/tradeDefencesActions';

import AssemblySessionModal from './AssemblySessionModal';

import global from "../../../../../global.scss";

const AssemblySessionPage = () => {
  const dispatch = useDispatch();
  const state = useSelector(state => state["business.core.tradeDefence"]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState(null);
  const [sortDirection, setSortDirection] = useState("asc");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTitle, setModalTitle] = useState("");
  const [update, setUpdate] = useState(null);

  const [sessionsList, setSessionsList] = useState([]);
  const [sessionsTotal, setSessionsTotal] = useState(0);

  const [menuOpen, setMenuOpen] = useState(false);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const [
    page,
    rowsPerPage,
    handleChangePage,
    handleChangeRowsPerPage
  ] = useTablePaginationInit();

  const tableHeadingsList = [
    { label: "Id", sort: "id" },
    { label: "Company id", sort: "ref_company_id" },
    { label: "Is active", sort: "is_active" },
    { label: "Time stamps", sort: "created_at" },
    { label: "", sort: null },
  ];

  const handleSort = (value) => {
    setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    setSort(value);
  };

  useEffect(() => {
    handleGetSessions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, search, sort, sortDirection]);

  useEffect(() => {
    try {
      setSessionsList(state.assemblySessionsList.records || []);
      setSessionsTotal(state.assemblySessionsList.total || 0);
    } catch (err) {}
  }, [state]);

  async function handleGetSessions(customLoading) {
    let query = "";
    if (search) query += `&search=${search}`;
    if (sort) query += `&ordering=${sortDirection === "asc" ? '' : '-'}${sort}`;
    dispatch(GetAssemblySessionsBIZcoreTD(page + 1, rowsPerPage, query, true, customLoading ? customLoading : setLoading));
  };

  return (
    <Box>

      <AssemblySessionModal
        open={modalOpen}
        setOpen={setModalOpen}
        title={modalTitle}
        update={update}
        handleGet={handleGetSessions}
      />

      <TopBar
        title={"Assembly sessions"}
        search={search}
        onSearchChange={(e) => {
          setSearch(e.target.value);
        }}
        // button1Text={"Add assembly session"}
        // button1OnClick={() => {
        //   setUpdate({ defaultMode: 1 });
        //   setModalTitle("Add assembly session");
        //   setModalOpen(!modalOpen);
        // }}
      />

      {
        loading
        ?
        <Box height={"300px"} className="flexCenterCenterRow">
          <CircularProgress />
        </Box>
        :
          sessionsList.length === 0
          ?
          <Box height={"300px"} className="flexCenterCenterRow">
            <Typography>No assembly session found</Typography>
          </Box>
          :
          <Table className="block">
            <TableHead>
              <TableRow>
                {
                  tableHeadingsList.map((heading, headingIndex) => (
                    heading.sort === null
                    ?
                    <TableCell key={headingIndex}>{heading.label}</TableCell>
                    :
                    <TableCell key={headingIndex}>
                      <TableSortLabel
                        active={sort === heading.sort}
                        direction={sortDirection}
                        onClick={() => handleSort(heading.sort)}
                      >
                        {heading.label}
                      </TableSortLabel>
                    </TableCell>
                  ))
                }
              </TableRow>
            </TableHead>
            <TableBody>
              {
                sessionsList.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption' color={"grey"}>
                        <Copyable>{row.ref_company_id || <NA />}</Copyable>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {
                        row.is_active === true
                        ? <Chip size="small" color="success" label="Yes" />
                        : row.is_active === false
                          ? <Chip size="small" color="error" label="No" />
                          : <NA />
                      }
                    </TableCell>
                    <TableCell>
                      <TableTimeStamps
                        createdAt={row.created_at}
                        updatedAt={row.updated_at}
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        onClick={(e) => {
                          setMenuAnchor(e.currentTarget);
                          setMenuOpen(true);
                          setUpdate(row);
                          setModalTitle(row.id || "");
                        }}>
                        <MoreVertIcon sx={{ color: global['secondary_light'] }} />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              }
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  sx={{ borderBottom: 0 }}
                  rowsPerPageOptions={[5, 10, 25, 50, 100]}
                  colSpan={5}
                  count={sessionsTotal}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  SelectProps={{
                    inputProps: { 'aria-label': 'rows per page' },
                    native: true
                  }}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                  ActionsComponent={TablePaginationActions}
                />
              </TableRow>
            </TableFooter>
          </Table>
      }

      <Menu
        open={menuOpen}
        anchorEl={menuAnchor}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          onClick={() => {
            setModalTitle("Edit assembly session");
            setUpdate({ ...update, defaultMode: 2 });
            setMenuOpen(false);
            setModalOpen(!modalOpen);
          }}>
          <Box className="flexCenter_Row" gap={"10px"}>
            <EditOutlinedIcon />
            Edit
          </Box>
        </MenuItem>
      </Menu>

    </Box>
  );
};

export default AssemblySessionPage;
